/**
 * iKRDiagram Main Application Component
 */

import '../../../ui-styles/ibest-style.css';
import { useMemo } from 'react';
import { defineFields, mergeInitialValues, useAnalysisWorkflow, useAppStorage, useCalcId, useLivePlotPrintImages, useRHFWithZod, useSavedCalculationLoader, useUrlFormValues } from '../shared';
import { analyze } from './api';
import type { ChartKey, KRDiagramResultState } from './appConfig';
import { APP_NAME, APP_VERSION, APP_VERSION_POLICY, LIVE_PLOT_CARD_MIN_HEIGHT, LIVE_PLOT_SIZE_BY_KEY, LOAD_TYPES, PRINT_CHART_ASPECT, PRINT_CHART_IMAGE_OPTIONS, PRINT_CHART_ORDER } from './appConfig';
import { schema } from './validation';
import type { FormValues, ParsedValues } from './validation';
import { ProjectInput, PrintPortal, FormFields, GenerateLinkButton, LivePlot, PrintChartImageGrid, RichSummaryReport } from '../shared';
import { generateShareableURL } from '../shared/urlUtils';

export default function App() {
  // Check for calcId first (short URL from CalcPad)
  const calcId = useCalcId();
  const urlFormValues = useUrlFormValues(calcId);

  const initialValues: FormValues = useMemo(
    () =>
      mergeInitialValues(
        {
          project: 'Default',
          title: 'KR Diagram',
          length: '10',
          length_o: '5',
          width: '5',
          weight: '12*5*10',
          klmFactor: '0.72',
          resistance: '9',
          deflection: '1.0',
          loadType: '0',
          pressure: '4',
          impulse: '28',
          ductility: '3',
          rotation: '3'
        },
        urlFormValues
      ),
    [urlFormValues]
  );

  const {
    register,
    formState: { errors },
    watch,
    reset
  } = useRHFWithZod<FormValues>({ schema, defaultValues: initialValues });

  const values = watch();
  const { saveDataToDB } = useAppStorage<FormValues, KRDiagramResultState>(APP_NAME, APP_VERSION);
  const { analysisResult, setAnalysisResult, isAnalyzing, saveStatus, handleAnalyze, handleSave } = useAnalysisWorkflow<FormValues, ParsedValues, KRDiagramResultState>({
    errors,
    values,
    schema,
    analyze,
    saveDataToDB
  });

  const { chartRefs, printRootRef } = useLivePlotPrintImages<ChartKey, KRDiagramResultState>({
    analysisResult,
    setAnalysisResult,
    getImageOptions: key => PRINT_CHART_IMAGE_OPTIONS[key],
    errorMessage: 'Unable to prepare KR diagram charts for printing. Please wait for the charts to finish rendering and try again.'
  });

  // Load full state from IndexedDB if calcId is present
  useSavedCalculationLoader<FormValues, KRDiagramResultState>(calcId, reset, setAnalysisResult, { appName: APP_NAME, versionPolicy: APP_VERSION_POLICY });

  /* prettier-ignore */
  const fields = defineFields<FormValues>([
    {kind: 'input', name: 'title', label: 'Title', helpText: 'Enter title', placeholder: 'KR Diagram'},
    {kind: 'input', name: 'length', label: 'Length', unit: 'ft', helpText: 'Overall member length', placeholder: '10'},
    {kind: 'input', name: 'length_o', label: 'Hinge Length', unit: 'ft', helpText: 'Hinge distance from support', placeholder: '5'},
    {kind: 'input', name: 'width', label: 'Tributary Width', unit: 'ft', helpText: 'Tributary width', placeholder: '10'},
    {kind: 'input', name: 'weight', label: 'Total Weight', unit: 'lbs', helpText: 'Total/added weight expression', placeholder: '10*5*12'},
    {kind: 'input', name: 'klmFactor', label: 'KLM Factor', unit: '', helpText: 'Effective mass factor (0-1)', placeholder: '0.72'},
    {kind: 'input', name: 'resistance', label: 'Ultimate Resistance', unit: 'kips', helpText: 'Ru value', placeholder: '9'},
    {kind: 'input', name: 'deflection', label: 'Elastic Deflection', unit: 'in', helpText: 'Xe value', placeholder: '1.0'},
    {kind: 'dropdown', name: 'loadType', label: 'Load Type', options: LOAD_TYPES, helpText: 'Select load type'},
    {kind: 'input', name: 'pressure', label: 'Peak Pressure', unit: 'psi', helpText: 'Pa value', placeholder: '1'},
    {kind: 'input', name: 'impulse', label: 'Impulse', unit: 'psi-msec', helpText: 'Ia value', placeholder: '10'},
    {kind: 'input', name: 'ductility', label: 'Ductility Limit', unit: '', helpText: 'Mu limit', placeholder: '5'},
    {kind: 'input', name: 'rotation', label: 'Rotation Limit', unit: 'deg', helpText: 'Theta limit', placeholder: '3'}
  ]);

  return (
    <>
      <div className="analysis-app analysis-app--teal analysis-app__layout layout-3col print:hidden w-full">
        <div className="analysis-app__hero">
          <div>
            <h2 className="analysis-app__title">{APP_NAME}</h2>
            <p className="analysis-app__subtitle">KR Diagram Analysis</p>
            <span>
              The KR diagram and the PI diagram are related plots, but they take different approaches. Whereas the PI diagram depicts response limits for a fixed structure for varying pressures (P) and impulses (I), the KR diagram illustrates response limits for fixed pressure and impulse for varying structural properties, such as stiffness (K) and resistance (R). In KR plots, a structure is
              considered acceptable if its stiffness (K) and resistance(R) are above both the ductility and the rotation limit lines. On the other hand, in the PI diagram, a structure's response is deemed acceptable if both pressure (P) and impulse (I) are below the limit line.
            </span>
          </div>
        </div>

        <div className="analysis-app__controls flex flex-col rounded-xl p-1 m-1 gap-y-0 shadow-div">
          <ProjectInput error={errors.project?.message ?? null} {...register('project')} />

          <FormFields<FormValues> fields={fields} register={register} errors={errors} />

          <div className="analysis-app__actions flex flex-row w-full mt-1 gap-2">
            <button className="submit-button" onClick={handleAnalyze} disabled={Object.keys(errors).length > 0 || isAnalyzing}>
              {isAnalyzing ? 'Analyzing...' : 'Analyze'}
            </button>
            <button className="submit-button" onClick={handleSave} disabled={Object.keys(errors).length > 0 || saveStatus === 'saving'}>
              {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'success' ? 'Saved!' : saveStatus === 'error' ? 'Error!' : 'Save'}
            </button>
            <GenerateLinkButton formData={values} />
          </div>
        </div>

        <div className="analysis-app__results-wrap grid min-w-0 grid-rows-[auto_1fr] rounded-xl gap-y-0 w-full">
          <div className="analysis-app__results shadow-div rounded-xl m-1 min-w-0 overflow-hidden">
            {analysisResult?.summary ? <RichSummaryReport summary={analysisResult.summary} className="rounded-lg border bg-white p-4 text-[13px] leading-5 text-slate-800" /> : <div className="p-4 text-black-500 italic">{analysisResult ? 'Saved result does not include a summary. Re-run the analysis to generate it.' : 'No analysis results yet.'}</div>}
          </div>

          <div className="analysis-app__charts flex flex-col shadow-div my-2 m-1 rounded-xl">
            {analysisResult?.plotData && (
              <div className="analysis-app__charts-grid grid grid-cols-1 gap-3 m-2 sm:m-4 xl:grid-cols-2 print:gap-1">
                {Object.entries(analysisResult.plotData)
                  .filter(([, plot]) => !!plot)
                  .map(([key, plot]) => {
                    const chartKey = key as ChartKey;

                    return (
                      <div key={key} className="analysis-app__chart-card flex min-w-0 items-center justify-center break-inside-avoid" style={{ minHeight: LIVE_PLOT_CARD_MIN_HEIGHT }}>
                        <LivePlot
                          ref={node => {
                            chartRefs.current[chartKey] = node;
                          }}
                          plot={plot}
                          {...LIVE_PLOT_SIZE_BY_KEY[chartKey]}
                        />
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
        <div className="print:hidden hidden lg:block w-full"></div>
      </div>

      {/* ---- PRINT PORTAL ---- */}
      <PrintPortal>
        <div ref={printRootRef} className="print-root">
          <div className="print-a4-landscape border border-black box-border bg-white">
            <div className="hidden print:grid h-full min-h-0 grid-cols-[24%_76%] print:bg-white print:text-black">
              <div className="flex flex-col h-full min-h-0">
                <div className="p-1">
                  <div className="text-[10px] font-semibold m-0 p-0 leading-none">{values.title || 'iKR Diagram'}</div>
                  <div className="text-[10px] opacity-70 m-0 p-0 leading-none mb-1">Project: {values.project || '—'}</div>
                </div>
                <div className="overflow-hidden border-t">
                  <div className="p-1">
                    <RichSummaryReport summary={analysisResult?.summary} className="text-[9.5px] leading-[1.18] text-black" />
                  </div>
                </div>

                {/* Shareable Link at Bottom */}
                <div className="grid grid-cols-2 mt-auto p-1 border-t">
                  <a href={generateShareableURL(values, window.location.pathname)} className="text-[9px] text-blue-600 underline font-semibold hover:text-blue-800" target="_blank" rel="noopener noreferrer" title="Click to open shareable link">
                    LINK
                  </a>
                  <div className="text-[10px] font-semibold m-0 p-0 leading-none text-blue-500">{`${APP_NAME} V${APP_VERSION}`}</div>
                </div>
              </div>

              <div className="h-full min-h-0 overflow-hidden border-l">
                <div className="h-full min-h-0 overflow-hidden">
                  <PrintChartImageGrid charts={analysisResult?.charts} order={PRINT_CHART_ORDER} aspectByKey={PRINT_CHART_ASPECT} fitPage />
                </div>
              </div>
            </div>
          </div>
        </div>
      </PrintPortal>
    </>
  );
}
